import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { getExam, createExam, updateExam } from "../../api/examApi";
import { getGrades } from "../../api/gradeApi";
import { getSubjects } from "../../api/subjectApi";
import { getSessions } from "../../api/sessionApi";

const examTypes = [
  { label: "Quiz", value: "quiz" },
  { label: "Midterm", value: "midterm" },
  { label: "Final", value: "final" },
  { label: "Project", value: "project" },
  { label: "Assignment", value: "assignment" },
  { label: "Other", value: "other" },
];

const ExamFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: "",
    exam_type: "quiz",
    grade_id: null,
    subject_id: null,
    session_id: null,
    exam_date: null,
    total_marks: 100,
    passing_marks: 40,
    weight: 1,
  });
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getGrades(), getSubjects(), getSessions()])
      .then(([g, s, ses]) => {
        setGrades(Array.isArray(g) ? g : g?.data || g?.items || []);
        setSubjects(Array.isArray(s) ? s : s?.data || s?.items || []);
        setSessions(Array.isArray(ses) ? ses : ses?.data || ses?.items || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    getExam(id).then((data) => {
      if (data?.success) {
        const e = data.exam;
        setForm({
          name: e.name || "",
          exam_type: e.exam_type || "quiz",
          grade_id: e.grade_id,
          subject_id: e.subject_id,
          session_id: e.session_id,
          exam_date: e.exam_date ? new Date(e.exam_date) : null,
          total_marks: e.total_marks || 100,
          passing_marks: e.passing_marks || 40,
          weight: e.weight || 1,
        });
      }
    });
  }, [id, isEdit]);

  const handleSave = async () => {
    if (!form.name || !form.grade_id || !form.subject_id) {
      toast.error("Name, grade, and subject are required");
      return;
    }
    try {
      setSaving(true);
      if (isEdit) {
        await updateExam(id, form);
        toast.success("Exam updated");
      } else {
        await createExam(form);
        toast.success("Exam created");
      }
      navigate("/index/exams");
    } catch (e) {
      toast.error("Failed to save exam");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2>{isEdit ? "Edit" : "New"} Exam</h2>
      <div className="formgrid grid">
        <div className="field col-12 md:col-8">
          <label>Name *</label>
          <InputText
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full"
          />
        </div>
        <div className="field col-12 md:col-4">
          <label>Type</label>
          <Dropdown
            value={form.exam_type}
            options={examTypes}
            onChange={(e) => setForm({ ...form, exam_type: e.value })}
            className="w-full"
          />
        </div>
        <div className="field col-12 md:col-4">
          <label>Grade *</label>
          <Dropdown
            value={form.grade_id}
            options={grades}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setForm({ ...form, grade_id: e.value })}
            className="w-full"
            placeholder="Select"
          />
        </div>
        <div className="field col-12 md:col-4">
          <label>Subject *</label>
          <Dropdown
            value={form.subject_id}
            options={subjects}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setForm({ ...form, subject_id: e.value })}
            className="w-full"
            placeholder="Select"
          />
        </div>
        <div className="field col-12 md:col-4">
          <label>Session</label>
          <Dropdown
            value={form.session_id}
            options={sessions}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setForm({ ...form, session_id: e.value })}
            className="w-full"
            placeholder="Select"
          />
        </div>
        <div className="field col-12 md:col-4">
          <label>Date</label>
          <Calendar
            value={form.exam_date}
            onChange={(e) => setForm({ ...form, exam_date: e.value })}
            showIcon
            dateFormat="yy-mm-dd"
            className="w-full"
          />
        </div>
        <div className="field col-12 md:col-4">
          <label>Total Marks</label>
          <InputNumber
            value={form.total_marks}
            onValueChange={(e) => setForm({ ...form, total_marks: e.value })}
            className="w-full"
          />
        </div>
        <div className="field col-12 md:col-4">
          <label>Passing Marks</label>
          <InputNumber
            value={form.passing_marks}
            onValueChange={(e) => setForm({ ...form, passing_marks: e.value })}
            className="w-full"
          />
        </div>
      </div>
      <div className="flex justify-content-end gap-2 mt-3">
        <Button label="Cancel" className="p-button-text" onClick={() => navigate("/index/exams")} />
        <Button label="Save" icon="pi pi-save" onClick={handleSave} loading={saving} />
      </div>
    </Card>
  );
};

export default ExamFormPage;
